import React, {useEffect, useState} from 'react';
import {
    Button,
    Descriptions,
    Form,
    Input,
    message,
    Modal,
    Popconfirm,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import {DeleteOutlined, EyeOutlined} from '@ant-design/icons';
import {ContactMessageItem, contactMessageService} from '../../services/contactMessageService';
import {useSearchParams} from 'react-router-dom';

const {Title, Text} = Typography;
const {TextArea} = Input;
const {Option} = Select;

const ContactMessageManagement: React.FC = () => {
    const [list, setList] = useState<ContactMessageItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedItem, setSelectedItem] = useState<ContactMessageItem | null>(null);
    const [statusModalVisible, setStatusModalVisible] = useState(false);
    const [form] = Form.useForm();
    const [searchParams] = useSearchParams();

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });

    useEffect(() => {
        loadList();
    }, []);

    useEffect(() => {
        const highlightId = searchParams.get('highlight');
        if (highlightId && list.length > 0) {
            const found = list.find(item => item.id === Number(highlightId));
            if (found) {
                handleViewDetail(found);
            }
        }
    }, [searchParams, list]);

    const loadList = async (page: number = 0, size: number = 20) => {
        setLoading(true);
        try {
            const data = await contactMessageService.getList(page, size);
            setList(data.content);
            setPagination(prev => ({
                ...prev,
                current: data.number + 1,
                total: data.totalElements,
                pageSize: data.size,
            }));
        } catch (error) {
            message.error('获取联系我列表失败');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetail = async (item: ContactMessageItem) => {
        setSelectedItem(item);
        setDetailModalVisible(true);
    };

    const handleUpdateStatus = (item: ContactMessageItem) => {
        setSelectedItem(item);
        form.setFieldsValue({status: item.status});
        setStatusModalVisible(true);
    };

    const handleStatusSubmit = async () => {
        try {
            const values = await form.validateFields();
            await contactMessageService.updateStatus(selectedItem!.id, values.status);
            message.success('更新成功');
            setStatusModalVisible(false);
            loadList(pagination.current - 1);
        } catch (error: any) {
            message.error(error.message || '操作失败');
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await contactMessageService.delete(id);
            message.success('删除成功');
            loadList(pagination.current - 1);
        } catch (error) {
            message.error('删除失败');
        }
    };

    const getStatusTag = (status: string) => {
        switch (status) {
            case 'PENDING':
                return <Tag color="orange">待处理</Tag>;
            case 'READ':
                return <Tag color="blue">已读</Tag>;
            case 'REPLIED':
                return <Tag color="green">已回复</Tag>;
            default:
                return <Tag>{status}</Tag>;
        }
    };

    const formatTime = (value: string) => {
        if (!value) return '-';
        return value.replace('T', ' ').substring(0, 19);
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
        },
        {
            title: '主题',
            dataIndex: 'subject',
            key: 'subject',
            width: 200,
            ellipsis: true,
        },
        {
            title: '称呼',
            dataIndex: 'salutation',
            key: 'salutation',
            width: 80,
        },
        {
            title: '联系邮箱',
            dataIndex: 'contactEmail',
            key: 'contactEmail',
            width: 200,
        },
        {
            title: '手机号',
            dataIndex: 'phone',
            key: 'phone',
            width: 140,
            render: (val: string) => val || '-',
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status: string) => getStatusTag(status),
        },
        {
            title: '创建时间',
            dataIndex: 'createdTime',
            key: 'createdTime',
            width: 180,
            render: (val: string) => formatTime(val),
        },
        {
            title: '操作',
            key: 'action',
            width: 160,
            render: (_: any, record: ContactMessageItem) => (
                <Space size={4}>
                    <Tooltip title="详情">
                        <Button type="text" icon={<EyeOutlined/>} onClick={() => handleViewDetail(record)}
                                size="small"/>
                    </Tooltip>
                    <Tooltip title="更新状态">
                        <Button type="text" onClick={() => handleUpdateStatus(record)} size="small">
                            {getStatusTag(record.status)}
                        </Button>
                    </Tooltip>
                    <Tooltip title="删除">
                        <Popconfirm title="确定删除该记录吗？" onConfirm={() => handleDelete(record.id)}>
                            <Button type="text" danger icon={<DeleteOutlined/>} size="small"/>
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Title level={4} style={{margin: 0}}>联系我</Title>
                <Text type="secondary">共 {pagination.total} 条记录</Text>
            </div>
            <Table
                columns={columns}
                dataSource={list}
                rowKey="id"
                loading={loading}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    onChange: (page, size) => loadList(page - 1, size),
                    showSizeChanger: true,
                    showTotal: (total) => `共 ${total} 条`,
                }}
            />

            <Modal
                title="消息详情"
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={null}
                width={600}
            >
                {selectedItem && (
                    <Descriptions column={2} bordered size="small">
                        <Descriptions.Item label="ID">{selectedItem.id}</Descriptions.Item>
                        <Descriptions.Item label="主题">{selectedItem.subject}</Descriptions.Item>
                        <Descriptions.Item label="称呼">{selectedItem.salutation}</Descriptions.Item>
                        <Descriptions.Item label="联系邮箱">{selectedItem.contactEmail}</Descriptions.Item>
                        <Descriptions.Item label="手机号">{selectedItem.phone || '-'}</Descriptions.Item>
                        <Descriptions.Item label="状态">{getStatusTag(selectedItem.status)}</Descriptions.Item>
                        <Descriptions.Item label="IP地址">{selectedItem.ipAddress || '-'}</Descriptions.Item>
                        <Descriptions.Item label="创建时间">{formatTime(selectedItem.createdTime)}</Descriptions.Item>
                        <Descriptions.Item label="更新时间"
                                           span={2}>{formatTime(selectedItem.updatedTime)}</Descriptions.Item>
                        <Descriptions.Item label="内容" span={2}>
                            <div style={{
                                whiteSpace: 'pre-wrap',
                                maxHeight: 300,
                                overflow: 'auto',
                                background: 'var(--pt-bg-card)',
                                padding: 12,
                                borderRadius: 6,
                                color: 'var(--pt-text-primary)'
                            }}>
                                {selectedItem.content}
                            </div>
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            <Modal
                title="更新状态"
                open={statusModalVisible}
                onOk={handleStatusSubmit}
                onCancel={() => setStatusModalVisible(false)}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="status" label="状态" rules={[{required: true, message: '请选择状态'}]}>
                        <Select>
                            <Option value="PENDING">待处理</Option>
                            <Option value="READ">已读</Option>
                            <Option value="REPLIED">已回复</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ContactMessageManagement;
